import React, {useState, useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {FaIcon} from '../../FaIcon';

function CopyButton({text}) {
    const {t} = useTranslation();
    const [copied, setCopied] = useState(false);

    const handleCopy = useCallback(() => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    }, [text]);

    return (
        <button className="copy-btn" onClick={handleCopy}>
            {copied ? t('dashbo_mcp.setup.copied') : <FaIcon iconName="copy"/>}
        </button>
    );
}

export function SetupGuideCard({icon, titleKey, descKey, noteKey, children}) {
    const {t} = useTranslation();
    const [expanded, setExpanded] = useState(false);

    return (
        <div className="setup-guide-card">
            <div className="card-header-row" onClick={() => setExpanded(!expanded)}>
                <div className="card-logo">
                    {typeof icon === 'string' ? <FaIcon iconName={icon}/> : icon}
                </div>
                <div className="card-info">
                    <h4>{t(titleKey)}</h4>
                    <p>{t(descKey)}</p>
                </div>
                <span className={`expand-icon ${expanded ? 'expanded' : ''}`}>
                    <FaIcon iconName="chevron-down"/>
                </span>
            </div>
            {expanded && <div className="card-body">
                {noteKey && <div className="setup-guide-note">
                    <FaIcon iconName="exclamation-triangle"/> {t(noteKey)}
                </div>}
                {children}
            </div>}
        </div>
    );
}

export function Step({number, children}) {
    return (
        <div className="step">
            <span className="step-number">{number}</span>
            <div className="step-content">{children}</div>
        </div>
    );
}

export function CodeBlock({children}) {
    const text = typeof children === 'string' ? children : '';
    return (
        <div className="code-block">
            <CopyButton text={text}/>
            {children}
        </div>
    );
}

export function UrlBlock({children}) {
    return <span className="url-block">{children}</span>;
}

export function OptionalPromptStep({promptText, children}) {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);
    const [promptExpanded, setPromptExpanded] = useState(false);
    const [copied, setCopied] = useState(false);

    const lines = promptText ? promptText.split('\n') : [];
    const previewLines = lines.slice(0, 5).join('\n');
    const hasMore = lines.length > 5;

    const handleCopy = useCallback(() => {
        if (!promptText) return;
        navigator.clipboard.writeText(promptText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    }, [promptText]);

    if (!promptText) return null;

    return (
        <div className="optional-prompt-step">
            <button className="optional-step-toggle" onClick={() => setOpen(!open)}>
                <FaIcon iconName={open ? 'chevron-down' : 'chevron-right'}/>
                {t('dashbo_mcp.setup.optional_prompt_title')}
            </button>
            {open && (
                <div className="optional-step-content">
                    <p className="optional-step-hint">{children}</p>
                    <button className="btn -alt -negative btn-sm" onClick={handleCopy}>
                        {copied ? t('dashbo_mcp.setup.copied') : t('dashbo_mcp.setup.copy_prompt')}
                    </button>
                    <div className="prompt-preview">
                        <pre className="prompt-preview-text">
                            {promptExpanded ? promptText : previewLines}{!promptExpanded && hasMore ? '...' : ''}
                        </pre>
                        {hasMore && (
                            <button className="prompt-expand-btn" onClick={() => setPromptExpanded(!promptExpanded)}>
                                {promptExpanded
                                    ? t('dashbo_mcp.setup.collapse_prompt')
                                    : t('dashbo_mcp.setup.expand_prompt', {count: lines.length})}
                            </button>
                        )}
                    </div>
                    <p className="optional-step-note">{t('dashbo_mcp.setup.prompt_benefit_note')}</p>
                </div>
            )}
        </div>
    );
}

export function OptionalSkillStep() {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);

    return (
        <div className="optional-prompt-step">
            <button className="optional-step-toggle" onClick={() => setOpen(!open)}>
                <FaIcon iconName={open ? 'chevron-down' : 'chevron-right'}/>
                {t('dashbo_mcp.setup.optional_skill_title')}
            </button>
            {open && (
                <div className="optional-step-content">
                    <p className="optional-step-hint">{t('dashbo_mcp.setup.skill_if_nodejs')}</p>
                    <CodeBlock>npx skills add https://github.com/fede-brc/dashbo-skills --skill dashbo-agencies</CodeBlock>
                    <p className="optional-step-hint" style={{marginTop: '0.6em'}}>{t('dashbo_mcp.setup.skill_if_no_nodejs')}</p>
                    <CodeBlock>mkdir -p .claude/skills/dashbo-agencies && curl -sL https://raw.githubusercontent.com/fede-brc/dashbo-skills/main/skills/dashbo-agencies/SKILL.md -o .claude/skills/dashbo-agencies/SKILL.md</CodeBlock>
                    <p className="optional-step-hint" style={{marginTop: '0.6em'}}>{t('dashbo_mcp.setup.skill_description')}</p>
                    <p className="optional-step-note">{t('dashbo_mcp.setup.skill_benefit_note')}</p>
                </div>
            )}
        </div>
    );
}
